import { computed, reactive, ref } from "vue";
import {
  calc,
  fmtTime,
  schedule,
  total,
  withDerivedDrinks,
  type CalcInput,
  type Discipline,
  type Line,
  type ScheduleEntry,
} from "@/domain/calc";
import {
  allPlans,
  getPlan,
  removePlan,
  savePlan as savePlanToStore,
  type PlanRecord,
} from "@/domain/store";
import {
  CATALOGUE,
  carbsOf,
  findProduct,
  groupCatalogue,
  makeLine,
  productName,
  type GroupKey,
} from "@/domain/catalogue";
import { verdictOf } from "@/domain/verdict";
import { I18N, type Lang, type Strings } from "@/domain/i18n";
import {
  LEG_DEFS,
  LEG_ICON,
  LIMITS,
  SPORT_LEGS,
  VESSEL_DEFAULTS,
  ordinal,
  type LegKey,
  type Sport,
} from "@/domain/legs";

/**
 * The whole of step 1's state. Kept as one composable rather than a store: the
 * prototype gets by on local state and there is no cross-route sharing yet.
 */

export interface LegInput {
  durMinMin: number;
  durMinMax: number;
  bottleMl: number;
  cupMl: number;
  stations: number;
  drinks: Line[];
  gels: Line[];
  bars: Line[];
}

/** Opening product selection per leg, from LEG_DEFAULTS in mvp-index.html. */
const LEG_PRODUCTS: Record<LegKey, Record<GroupKey, [string, number][]>> = {
  swim: { drinks: [], gels: [["maurten-gel-100", 1]], bars: [["no-bar", 0]] },
  bike: {
    drinks: [["water", 1], ["pwbar-isoactive", 1]],
    gels: [["powerbar-gel", 5]],
    bars: [["powerbar-energize", 1]],
  },
  run: {
    drinks: [["water", 11], ["cola", 1]],
    gels: [["powerbar-hydro", 6]],
    bars: [["no-bar", 0]],
  },
  run2: { drinks: [["water", 5]], gels: [["powerbar-gel", 2]], bars: [["no-bar", 0]] },
};

/** Maximum product lines per group (FR-2.3). */
export const MAX_LINES = 3;

export interface LegLabel {
  key: LegKey;
  index: number;
  type: Discipline;
  icon: string;
  name: string;
  stage: string;
  meanMinutes: number;
  current: boolean;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

function defaultLegs(): Record<LegKey, LegInput> {
  const entries = Object.entries(LEG_DEFS).map(([key, def]) => {
    const vessel = {
      discipline: def.type,
      bottleMl: VESSEL_DEFAULTS.bottleMl,
      cupMl: VESSEL_DEFAULTS.cupMl,
    };
    const lines = (group: GroupKey): Line[] =>
      LEG_PRODUCTS[key as LegKey][group].flatMap(([id, count]) => {
        const product = findProduct(group, id);
        return product ? [makeLine(group, product, vessel, count)] : [];
      });
    return [
      key,
      {
        durMinMin: def.durMinMin,
        durMinMax: def.durMinMax,
        bottleMl: VESSEL_DEFAULTS.bottleMl,
        cupMl: VESSEL_DEFAULTS.cupMl,
        stations: VESSEL_DEFAULTS.stations,
        drinks: lines("drinks"),
        gels: lines("gels"),
        bars: lines("bars"),
      },
    ];
  });
  return Object.fromEntries(entries) as Record<LegKey, LegInput>;
}

export function usePlan() {
  const lang = ref<Lang>("de");
  const sport = ref<Sport>("triathlon");
  const legIndex = ref(0);
  const weight = ref(75);
  // Keyed by leg, not by discipline: switching sport keeps what you typed
  // for a leg you had already filled in (FR-1.10).
  const legs = reactive(defaultLegs());

  const t = computed<Strings>(() => I18N[lang.value]);
  const order = computed<LegKey[]>(() => SPORT_LEGS[sport.value]);
  const legKey = computed<LegKey>(
    () => order.value[Math.min(legIndex.value, order.value.length - 1)]!,
  );
  const leg = computed<LegInput>(() => legs[legKey.value]);
  const legType = computed<Discipline>(() => LEG_DEFS[legKey.value].type);

  const nameOf = (key: LegKey): string => t.value[LEG_DEFS[key].nameKey];
  const stageOf = (key: LegKey): string => t.value[LEG_DEFS[key].stageKey];

  const labels = computed<LegLabel[]>(() =>
    order.value.map((key, index) => {
      const suffix = ordinal(order.value, index);
      const input = legs[key];
      return {
        key,
        index,
        type: LEG_DEFS[key].type,
        icon: LEG_ICON[LEG_DEFS[key].type],
        name: nameOf(key) + suffix,
        stage: stageOf(key) + suffix,
        meanMinutes: Math.round((input.durMinMin + input.durMinMax) / 2),
        current: index === legIndex.value,
      };
    }),
  );

  const current = computed<LegLabel>(() => labels.value[legIndex.value]!);
  const isLastLeg = computed(() => legIndex.value >= order.value.length - 1);

  /** 1.0-1.3 g per kg body weight, rounded for display (FR-1.5). */
  const ceiling = computed(() => ({
    min: Math.round(weight.value * 1.0),
    max: Math.round(weight.value * 1.3),
  }));

  const totalMeanMinutes = computed(() =>
    labels.value.reduce((sum, l) => sum + l.meanMinutes, 0),
  );
  const totalDuration = computed(() => fmtTime(totalMeanMinutes.value));

  /** FR-1.7. The wireframe does not enforce it; the real build must. */
  const durationError = computed(() =>
    leg.value.durMinMin > leg.value.durMinMax ? t.value.durationOrder : "",
  );
  const canAdvance = computed(() => durationError.value === "");

  /** FR-1.9: "Weiter zum Radteil", or "Weiter zur Verpflegung" on the last leg. */
  const nextLabel = computed(() => {
    if (isLastLeg.value) return t.value.nextToFuel;
    const next = labels.value[legIndex.value + 1];
    return `${t.value.nextTo} ${next?.stage ?? ""}`.trim();
  });

  function setSport(next: Sport) {
    sport.value = next;
    // FR-1.10: back to the first leg, per-leg values untouched.
    legIndex.value = 0;
    touch();
  }

  function setWeight(next: number) {
    weight.value = clamp(Math.round(next), LIMITS.weight.min, LIMITS.weight.max);
    touch();
  }

  function stepWeight(delta: number) {
    setWeight(weight.value + delta * LIMITS.weight.step);
  }

  function setDuration(field: "durMinMin" | "durMinMax", next: number) {
    legs[legKey.value][field] = clamp(
      Math.round(next),
      LIMITS.duration.min,
      LIMITS.duration.max,
    );
    touch();
  }

  function stepDuration(field: "durMinMin" | "durMinMax", delta: number) {
    setDuration(field, leg.value[field] + delta * LIMITS.duration.step);
  }

  function setVessel(field: "bottleMl" | "cupMl" | "stations", next: number) {
    legs[legKey.value][field] = Math.max(0, Math.round(next) || 0);
    touch();
  }

  function selectLeg(index: number) {
    legIndex.value = clamp(index, 0, order.value.length - 1);
  }

  function advance(): "next-leg" | "next-step" | "blocked" {
    if (!canAdvance.value) return "blocked";
    if (isLastLeg.value) return "next-step";
    legIndex.value += 1;
    return "next-leg";
  }

  function retreat(): "prev-leg" | "prev-step" {
    if (legIndex.value > 0) {
      legIndex.value -= 1;
      return "prev-leg";
    }
    return "prev-step";
  }

  function setLang(next: Lang) {
    lang.value = next;
    if (typeof document !== "undefined") document.documentElement.lang = next;
  }

  // ---------------------------------------------------------------- step 2 --

  const vessel = computed(() => ({
    discipline: legType.value,
    bottleMl: leg.value.bottleMl,
    cupMl: leg.value.cupMl,
  }));

  /** Products this leg may choose from, filtered per discipline (FR-2.8). */
  function catalogueFor(group: GroupKey) {
    return groupCatalogue(group, legType.value);
  }

  /** Grams a product contributes here -- derived, never stored (FR-2.7). */
  function gramsOf(group: GroupKey, id: string): number {
    const product = findProduct(group, id);
    return product ? carbsOf(group, product, vessel.value) : 0;
  }

  const fuelInput = computed<CalcInput>(() =>
    withDerivedDrinks(
      {
        discipline: legType.value,
        weight: weight.value,
        durMinMin: leg.value.durMinMin,
        durMinMax: leg.value.durMinMax,
        bottleMl: leg.value.bottleMl,
        cupMl: leg.value.cupMl,
        stations: leg.value.stations,
        drinks: leg.value.drinks,
        gels: leg.value.gels,
        bars: leg.value.bars,
      },
      CATALOGUE.drinks,
    ),
  );

  const fuel = computed(() => calc(fuelInput.value));

  /** FR-2.9: nothing chosen at all. */
  const isEmptySelection = computed(() =>
    [...leg.value.drinks, ...leg.value.gels, ...leg.value.bars].every((l) => l.count === 0),
  );

  const verdict = computed(() =>
    verdictOf({
      perHourAvg: fuel.value.perHour.avg,
      totalCarbs: fuel.value.totalCarbs,
      ceilingMin: weight.value * 1.0,
      ceilingMax: weight.value * 1.3,
      isSwim: legType.value === "swim",
      isEmpty: isEmptySelection.value,
      t: t.value,
    }),
  );

  const groupTotal = (group: GroupKey): number =>
    leg.value[group].reduce((sum, l) => sum + l.count, 0);

  function setLineProduct(group: GroupKey, index: number, id: string) {
    const product = findProduct(group, id);
    if (!product) return;
    const line = leg.value[group][index];
    if (!line) return;
    leg.value[group][index] = makeLine(group, product, vessel.value, line.count, lang.value);
    touch();
  }

  function setLineCount(group: GroupKey, index: number, count: number) {
    const line = leg.value[group][index];
    if (!line) return;
    line.count = clamp(Math.round(count) || 0, 0, 30);
    touch();
  }

  /** FR-2.3: prefilled with the first catalogue product, capped at three. */
  function addLine(group: GroupKey) {
    if (leg.value[group].length >= MAX_LINES) return;
    const first = catalogueFor(group)[0];
    if (!first) return;
    leg.value[group].push(makeLine(group, first, vessel.value, 1, lang.value));
    touch();
  }

  /** FR-2.3: a group never goes empty; the last line cannot be removed. */
  function removeLine(group: GroupKey, index: number) {
    if (leg.value[group].length <= 1) return;
    leg.value[group].splice(index, 1);
    touch();
  }

  function canAddLine(group: GroupKey): boolean {
    return leg.value[group].length < MAX_LINES;
  }

  function canRemoveLine(group: GroupKey): boolean {
    return leg.value[group].length > 1;
  }

  /** Product names are language-dependent for the generic entries. */
  function labelFor(group: GroupKey, id: string): string {
    const product = findProduct(group, id);
    return product ? productName(product, lang.value) : id;
  }

  // ---------------------------------------------------------------- step 3 --

  /** Every leg of the chosen discipline, computed. */
  const legResults = computed(() =>
    order.value.map((key, index) => {
      const input = legs[key];
      const type = LEG_DEFS[key].type;
      const flat = withDerivedDrinks(
        {
          discipline: type,
          weight: weight.value,
          durMinMin: input.durMinMin,
          durMinMax: input.durMinMax,
          bottleMl: input.bottleMl,
          cupMl: input.cupMl,
          stations: input.stations,
          drinks: input.drinks,
          gels: input.gels,
          bars: input.bars,
        },
        CATALOGUE.drinks,
      );
      const label = labels.value[index]!;
      return { key, index, type, label, flat, res: calc(flat) };
    }),
  );

  const totals = computed(() => total(legResults.value));

  /** Schedule for whichever leg the plan screen is showing. */
  const activeResult = computed(
    () => legResults.value.find((r) => r.key === legKey.value) ?? legResults.value[0]!,
  );

  const activeSchedule = computed<ScheduleEntry[]>(() =>
    schedule(activeResult.value.flat, activeResult.value.res),
  );

  const activeVerdict = computed(() =>
    verdictOf({
      perHourAvg: activeResult.value.res.perHour.avg,
      totalCarbs: activeResult.value.res.totalCarbs,
      ceilingMin: weight.value * 1.0,
      ceilingMax: weight.value * 1.3,
      isSwim: activeResult.value.type === "swim",
      isEmpty: activeResult.value.res.totalCarbs === 0,
      t: t.value,
    }),
  );

  const totalsVerdict = computed(() =>
    verdictOf({
      perHourAvg: totals.value.perHour,
      totalCarbs: totals.value.carbs,
      ceilingMin: weight.value * 1.0,
      ceilingMax: weight.value * 1.3,
      isSwim: false,
      isEmpty: totals.value.carbs === 0,
      t: t.value,
    }),
  );

  // ------------------------------------------------------------ saved plans --

  const plans = ref<PlanRecord[]>(allPlans());
  const activeId = ref<string | null>(null);
  const planName = ref("");
  const planDate = ref("");
  const dirty = ref(false);
  const toast = ref<string | null>(null);

  /** FR-4.3: any change to the inputs marks the plan unsaved. */
  function touch() {
    dirty.value = true;
  }

  const isSaved = computed(() => activeId.value !== null && !dirty.value);

  function persist(now: number = Date.now()): PlanRecord {
    const record = savePlanToStore(
      {
        ...(activeId.value ? { id: activeId.value } : {}),
        name: planName.value.trim() || t.value.planNameHint,
        date: planDate.value,
        sport: sport.value,
        weight: weight.value,
        legs: JSON.parse(JSON.stringify(legs)) as PlanRecord["legs"],
        totalCarbs: Math.round(totals.value.carbs * 10) / 10,
        perHour: Math.round(totals.value.perHour * 10) / 10,
      },
      now,
    );
    const wasUpdate = activeId.value !== null;
    plans.value = allPlans();
    activeId.value = record.id;
    dirty.value = false;
    toast.value = wasUpdate ? t.value.updatedToast : t.value.savedToast;
    return record;
  }

  function loadPlan(id: string): boolean {
    const record = getPlan(id);
    if (!record) return false;
    sport.value = record.sport;
    weight.value = record.weight;
    for (const [key, stored] of Object.entries(record.legs)) {
      if (stored) Object.assign(legs[key as LegKey], stored);
    }
    planName.value = record.name;
    planDate.value = record.date;
    activeId.value = record.id;
    dirty.value = false;
    legIndex.value = 0;
    return true;
  }

  function deletePlan(id: string) {
    plans.value = removePlan(id);
    if (id === activeId.value) {
      activeId.value = null;
      dirty.value = true;
    }
    toast.value = t.value.deletedToast;
  }

  function newPlan() {
    activeId.value = null;
    planName.value = "";
    planDate.value = "";
    dirty.value = true;
  }

  function clearToast() {
    toast.value = null;
  }

  return {
    // state
    lang,
    sport,
    legIndex,
    weight,
    legs,
    // derived
    t,
    order,
    legKey,
    leg,
    legType,
    labels,
    current,
    isLastLeg,
    ceiling,
    totalMeanMinutes,
    totalDuration,
    durationError,
    canAdvance,
    nextLabel,
    // actions
    setSport,
    setWeight,
    stepWeight,
    setDuration,
    stepDuration,
    setVessel,
    selectLeg,
    advance,
    retreat,
    setLang,
    // step 2
    vessel,
    fuel,
    verdict,
    isEmptySelection,
    catalogueFor,
    gramsOf,
    groupTotal,
    setLineProduct,
    setLineCount,
    addLine,
    removeLine,
    canAddLine,
    canRemoveLine,
    labelFor,
    // step 3
    legResults,
    totals,
    activeResult,
    activeSchedule,
    activeVerdict,
    totalsVerdict,
    // saved plans
    plans,
    activeId,
    planName,
    planDate,
    dirty,
    toast,
    isSaved,
    persist,
    loadPlan,
    deletePlan,
    newPlan,
    clearToast,
  };
}

export type Plan = ReturnType<typeof usePlan>;
